
import {
    ConsoleLogger,
    DefaultDeviceController,
    DefaultMeetingSession,
    LogLevel,
    MeetingSessionConfiguration,
    MeetingSessionStatusCode,
} from 'amazon-chime-sdk-js';

/**
 * @uses Chimeの処理を行うクラス
 */
export class AWSChime {

    sendLogCallback = null;
    cancelCallback = null;

    prefixId = null;
    config = null;

    session = null;

    timer = null;
    warnedAt = null;
    forceEndedAt = null;
    isEnded = true;

    videoElements = {};
    indexMap = {};

    logs = [];
    errors = [];

    selectedVideoInputDeviceId = null;

    constructor(prefixId, config, sendLogCallback, cancelCallback = null, isAttendeeOnly = false) {
        this.prefixId = prefixId;
        this.config = config;
        this.sendLogCallback = sendLogCallback;
        this.cancelCallback = cancelCallback;
        this.isAttendeeOnly = isAttendeeOnly;

        (async () => {

            const logLevel = LogLevel.DEBUG;
            // const logLevel = LogLevel.ERROR;

            const logger = new ConsoleLogger('MeetingLogs', logLevel);
            const deviceController = new DefaultDeviceController(logger);
            const sessionConfig = new MeetingSessionConfiguration(this.config.meeting, this.config.attendee);

            this.session = new DefaultMeetingSession(sessionConfig, logger, deviceController);

            try {
                await this.session.audioVideo.audioMixController.bindAudioElement(document.getElementById(this.config.audio_id));
                this.log('set audio', this.config.audio_id);
            } catch (e) {
                this.error(e.message);
                toastr.warning(this.config.errors.bind);
            }

            this.session.audioVideo.setDeviceLabelTrigger(async () =>
                await session.navigator.mediaDevices.getUserMedia({ audio: true, video: true })
            );

            const audioInputDevices = await this.session.audioVideo.listAudioInputDevices();
            const audioOutputDevices = await this.session.audioVideo.listAudioOutputDevices();
            const videoInputDevices = await this.session.audioVideo.listVideoInputDevices();

            if (audioInputDevices[0]) {
                this.log('start audio input', audioInputDevices[0].deviceId);
                try {
                    await this.session.audioVideo.startAudioInput(audioInputDevices[0].deviceId);
                } catch (e) {
                    this.error(e.message);
                    toastr.warning(this.config.errors.device);
                }
            }
            if (audioOutputDevices[0]) {
                this.log('choose audio output', audioOutputDevices[0].deviceId);
                try {
                    await this.session.audioVideo.chooseAudioOutput(audioOutputDevices[0].deviceId);
                } catch (e) {
                    this.error(e.message);
                    toastr.warning(this.config.errors.device);
                }
            }
            if (videoInputDevices[0]) {
                this.log('start video input', videoInputDevices[0].deviceId);
                try {
                    this.selectedVideoInputDeviceId = videoInputDevices[0].deviceId;
                    await this.session.audioVideo.startVideoInput(videoInputDevices[0].deviceId);
                } catch (e) {
                    this.error(e.message);
                    toastr.warning(this.config.errors.device);
                }
            }

            if (this.getElement('meeting-devices').length > 0) {
                this.setAudioInputSelectOption(audioInputDevices);
                this.setAudioOutputSelectOption(audioOutputDevices);
                this.setVideoInputSelectOption(videoInputDevices);
            }

            this.session.audioVideo.addDeviceChangeObserver(this.deviceChangeObserver(this));

            this.setDeviceEvent(this);

            this.session.audioVideo.chooseVideoInputQuality(this.config.size.width, this.config.size.height, 3, 1000);
            this.log(null, 'choose video input quality ...');

            for (let i = 0; i <= this.config.tiles; i++) {
                this.videoElements[i] = document.getElementById(this.config.video_id_prefix + i);
            }

            // tileの上限などにより取得が行えない場合
            if (!this.acquireVideoElement()) {
                this.error(this.config.errors.tile);
                toastr.warning(this.config.errors.tile);
            };

            this.session.audioVideo.addObserver(this.audioVideoObserver(this));
            this.session.audioVideo.start();
            this.log(null, 'audio video start ...');
            this.session.audioVideo.startLocalVideoTile();
            this.log(null, 'start local video tile ...');
            window.chimeInstance = this;
        })();

        this.setMeetingEvent();
    }

    async restart() {

        const audioInputDevices = await this.session.audioVideo.listAudioInputDevices();
        const audioOutputDevices = await this.session.audioVideo.listAudioOutputDevices();
        const videoInputDevices = await this.session.audioVideo.listVideoInputDevices();

        let audioInputDeviceId = ((audioInputDevices[0])) ? audioInputDevices[0].deviceId : null;
        let audioOutputDeviceId = ((audioOutputDevices[0])) ? audioOutputDevices[0].deviceId : null;
        let videoInputDeviceId = ((videoInputDevices[0])) ? videoInputDevices[0].deviceId : null;
        
        audioInputDeviceId = ($(create_id(this.prefixId, 'audio-inputs')).val()) ? $(create_id(this.prefixId, 'audio-inputs')).val() : audioInputDeviceId;
        audioOutputDeviceId = ($(create_id(this.prefixId, 'audio-outputs')).val()) ? $(create_id(this.prefixId, 'audio-outputs')).val() : audioOutputDeviceId;
        videoInputDeviceId = ($(create_id(this.prefixId, 'video-inputs')).val()) ? $(create_id(this.prefixId, 'video-inputs')).val() : videoInputDeviceId;

        if (audioInputDeviceId) {
            try {
                await this.session.audioVideo.startAudioInput(audioInputDeviceId);
            } catch (e) {
                this.error(e.message);
                toastr.warning(this.config.errors.device);
            }
        }

        if (audioOutputDeviceId) {
            try {
                await this.session.audioVideo.chooseAudioOutput(audioOutputDeviceId);
            } catch (e) {
                this.error(e.message);
                toastr.warning(this.config.errors.device);
            }
        }

        if (videoInputDeviceId) {
            try {
                this.selectedVideoInputDeviceId = videoInputDeviceId;
                await this.session.audioVideo.startVideoInput(videoInputDeviceId);
            } catch (e) {
                this.error(e.message);
                toastr.warning(this.config.errors.device);
            }
        }
        
    }

    getElement(suffix = '') {
        return $(create_id(this.prefixId, suffix));
    }

    getValue(suffix = '') {
        return this.getElement(suffix).val();
    }

    acquireVideoElement(tileId = null) {
        for (let i = 0; i <= this.config.tiles; i++) {
            if (this.indexMap[i] === tileId) {
                return this.videoElements[i];
            }
        }
        for (let i = 0; i <= this.config.tiles; i++) {
            if (!this.indexMap.hasOwnProperty(i)) {
                this.indexMap[i] = tileId;
                return this.videoElements[i];
            }
        }
        return;
    }

    mainVideoElement(videoElement) {
        const videoView = document.getElementById(this.config.video_view_id);
        const videoSlide = document.getElementById(this.config.video_slide_id);
        for (let i = 0; i < videoView.childElementCount; i++) {
            videoSlide.prepend(videoView.children[i]);
        }
        videoView.prepend(videoElement);
    }

    deviceChangeObserver(obj = null) {

        obj = (obj == null) ? this : obj;

        return {
            audioInputsChanged: freshAudioInputDeviceList => {
                obj.setAudioInputSelectOption(freshAudioInputDeviceList);
            },
            audioOutputsChanged: freshAudioOutputDeviceList => {
                obj.setAudioOutputSelectOption(freshAudioOutputDeviceList);
            },
            videoInputsChanged: freshVideoInputDeviceList => {
                obj.setVideoInputSelectOption(freshVideoInputDeviceList);
            },
        };
    }

    audioVideoObserver(obj = null) {

        obj = (obj == null) ? this : obj;

        return {
            audioVideoDidStart: () => {
                obj.log(null, 'audio video did start ...');
                obj.isEnded = false;
                
                if ($(create_id(obj.prefixId, 'timer')).length) {
                    _ajax.background(obj.getElement('timer').data('action'), {
                        'meeting_id': obj.getValue('meeting_id')
                    }, function (results, obj) {
                        obj.warnedAt = moment(results.warned_at);
                        obj.forceEndedAt = moment(results.forced_ended_at);
                        // 対応時間のタイマー
                        obj.timer = setInterval(obj.displayTimeUpdate, 1000, obj);
                    }, null, 'json', obj);
                }
                
            },
            audioVideoDidStop: sessionStatus => {
                obj.log(null, 'audio video did stop ...');
                obj.isEnded = true;
                if (obj.timer != null) {
                    clearInterval(obj.timer);
                }
                // const sessionStatusCode = sessionStatus.statusCode();
                // (sessionStatusCode === MeetingSessionStatusCode.MeetingEnded)
            },
            videoTileDidUpdate: tileState => {
                obj.log(null, 'video tile did update ...');
                if (!tileState.boundAttendeeId) {
                    return;
                }
                const videoElement = obj.acquireVideoElement(tileState.tileId);
                videoElement.dataset['attendee'] = tileState.boundAttendeeId;
                obj.session.audioVideo.bindVideoElement(tileState.tileId, videoElement);
                if (obj.isAttendeeOnly == true || !tileState.localTile) {
                    obj.mainVideoElement(videoElement);
                }
            },
            videoTileWasRemoved: tileId => {
                obj.log(null, 'video tile was removed ...');
                for (let i = 0; i <= obj.config.tiles; i++) {
                    if (obj.indexMap[i] === tileId) {
                        delete obj.indexMap[i];
                    }
                }
            }
        };
    }

    displayTimeUpdate(obj = null) {

        obj = (obj == null) ? this : obj;

        const timerElement = obj.getElement('timer');
        const recordingElement = obj.getElement('recording');
        const current = moment();
        const limit = Math.ceil(obj.forceEndedAt.diff(current) / 1000);
        const warning = Math.ceil(obj.warnedAt.diff(current) / 1000);
        if (limit > 0) {
            timerElement.text(second_to_time(limit));
            if (warning < 0) {
                timerElement.addClass('text-danger');
            }
        } else {
            timerElement.text(second_to_time(0));
            if (!obj.isEnded) {
                $(recordingElement).addClass('display-none');
                obj.end()
                obj.error(timerElement.data('message'));
                toastr.warning(timerElement.data('message'));
            }
        }
    }

    setMeetingEvent(obj = null) {

        obj = (obj == null) ? this : obj;

        // slideのビデオ表示押下時
        $(document).on('click', create_id(obj.config.video_slide_id, ' .video-preview'), function() {
            obj.mainVideoElement(document.getElementById($(this).attr('id')));
            obj.log('video preview did change', $(this).attr('id'));
        });

        // リロード・クローズ前にログを送信
        $(window).on('beforeunload', function() {
            (obj.sendLogCallback != null) && obj.sendLogCallback(obj.getValue('log'), obj.getValue('id'), obj);
            (obj.cancelCallback != null) && obj.cancelCallback();
        });

        // モーダル非表示時
        $(document).on('hidden.bs.modal', create_id(obj.prefixId), function() {
            (obj.sendLogCallback != null) && obj.sendLogCallback(obj.getValue('log'), obj.getValue('id'), obj);
            (obj.cancelCallback != null) && obj.cancelCallback();
            obj.end();
        });
    }

    setDeviceEvent(obj = null) {

        obj = (obj == null) ? this : obj;

        $(document).on('click', create_id(obj.prefixId, 'audio-inputs-options .dropdown-item'), async function() {
            const deviceId = $(this).data('device_id');
            obj.log('start audio input', deviceId);
            $(create_id(obj.prefixId, 'audio-inputs-options .dropdown-item')).toggleClass('selected', false);
            $(this).toggleClass('selected', true);
            try {
                await obj.session.audioVideo.startAudioInput(deviceId);
            } catch (e) {
                obj.error(e.message);
                toastr.warning(obj.config.errors.device);
            }
        });

        $(document).on('click', create_id(obj.prefixId, 'audio-outputs-options .dropdown-item'), async function() {
            const deviceId = $(this).data('device_id');
            obj.log('start audio output', deviceId);
            $(create_id(obj.prefixId, 'audio-outputs-options .dropdown-item')).toggleClass('selected', false);
            $(this).toggleClass('selected', true);
            try {
                await obj.session.audioVideo.chooseAudioOutput(deviceId);
            } catch (e) {
                obj.error(e.message);
                toastr.warning(obj.config.errors.device);
            }
            return false;
        });

        $(document).on('click', create_id(obj.prefixId, 'video-inputs-options .dropdown-item'), async function() {
            const deviceId = $(this).data('device_id');
            obj.log('start video input', deviceId);
            $(create_id(obj.prefixId, 'video-inputs-options .dropdown-item')).toggleClass('selected', false);
            $(this).toggleClass('selected', true);
            try {
                await obj.session.audioVideo.startVideoInput(deviceId);
            } catch (e) {
                obj.error(e.message);
                toastr.warning(obj.config.errors.device);
            }
            return false;
        });

        $(document).on('change', create_id(obj.prefixId, 'video-inputs'), async function() {
            obj.log('choose video input', $(this).val());
            try {
                await obj.session.audioVideo.startVideoInput($(this).val());
            } catch (e) {
                obj.error(e.message);
                toastr.warning(obj.config.errors.device);
            }
        });

    }

    setAudioInputSelectOption(audioInputDevices) {
        this.log('video input devices');
        let registeds = [];
        if (audioInputDevices.length > 0) {
            this.getElement('audio-inputs-options').empty();
            var defaultAudioInputGroupId = null;
            audioInputDevices.forEach(device => {
                if (device.deviceId == '') {
                    return;
                }
                if (device.deviceId == 'default') {
                    defaultAudioInputGroupId = device.groupId;
                } else {
                    // デバイスIDが登録済みの場合はスキップ
                    if (registeds.indexOf(device.deviceId) >= 0 || device.deviceId == 'communications') {
                        return;
                    }
                    this.log(null, '-' + device.deviceId + '(' + device.label + ')');
                    const option = $('<a></a>', {
                        'class': 'dropdown-item',
                        'data-device_id': device.deviceId,
                        'data-group': device.groupId,
                    }).text(device.label);
                    $(option).toggleClass('selected', (device.groupId == defaultAudioInputGroupId || audioInputDevices.length == 1));
                    this.getElement('audio-inputs-options').append(option);
                    registeds.push(device.deviceId);
                }
            });
        }
        this.getElement('audio-input-select').toggleClass('invalid', (audioInputDevices.length == 0));
    }

    

    setAudioOutputSelectOption(audioOutputDevices) {
        this.log('audio output devices');
        let registeds = [];
        if (audioOutputDevices.length > 0) {
            this.getElement('audio-outputs-options').empty();
            var defaultAudioOutputGroupId = null;
            audioOutputDevices.forEach(device => {
                if (device.deviceId == '') {
                    return;
                }
                if (device.deviceId == 'default') {
                    defaultAudioOutputGroupId = device.groupId;
                } else {
                    // デバイスIDが登録済みの場合はスキップ
                    if (registeds.indexOf(device.deviceId) >= 0 || device.deviceId == 'communications') {
                        return;
                    }
                    this.log(null, '-' + device.deviceId + '(' + device.label + ')');
                    const option = $('<a></a>', {
                        'class': 'dropdown-item',
                        'data-device_id': device.deviceId,
                        'data-group': device.groupId,
                    }).text(device.label);
                    $(option).toggleClass('selected', (device.groupId == defaultAudioOutputGroupId || audioOutputDevices.length == 1));
                    this.getElement('audio-outputs-options').append(option);
                    registeds.push(device.deviceId);
                }
            });
        }
        this.getElement('audio-output-select').toggleClass('invalid', (audioOutputDevices.length == 0));
    }

    setVideoInputSelectOption(videoInputDevices) {
        this.log('video input devices');
        let registeds = [];
        if (videoInputDevices.length > 0) {
            // 管理画面用
            this.getElement('video-inputs-options').empty();
            // クライアント用
            $('#remote_rollcall-api-join-video-inputs').empty();
            videoInputDevices.forEach(device => {
                if (device.deviceId == '') {
                    return;
                }
                if (device.deviceId == 'default') {
                    this.selectedVideoInputDeviceId = device.deviceId;
                } else {
                    this.log(null, '-' + device.deviceId + '(' + device.label + ')');
                    // デバイスIDが登録済みの場合はスキップ
                    if (registeds.indexOf(device.deviceId) >= 0 || device.deviceId == 'communications') {
                        return;
                    }
                    // 管理画面用
                    const cloudOption = $('<a></a>', {
                        'class': 'dropdown-item',
                        'data-device_id': device.deviceId,
                        'data-group': device.groupId,
                    }).text(device.label);
                    $(cloudOption).toggleClass('selected', (device.deviceId == this.selectedVideoInputDeviceId || videoInputDevices.length == 1));
                    this.getElement('video-inputs-options').append(cloudOption);
                    // クライアント用
                    const clientOption = $('<option></option>', {
                        'value': device.deviceId,
                        'data-group': device.groupId,
                    }).text(device.label);
                    $(clientOption).prop('selected', (device.deviceId == this.selectedVideoInputDeviceId));
                    $('#remote_rollcall-api-join-video-inputs').append(clientOption);
                    registeds.push(device.deviceId);
                }
            });
        }
        this.getElement('video-select').toggleClass('invalid', (videoInputDevices.length == 0));
        $('#remote_rollcall-api-join-video-inputs').parent().toggleClass('invalid', (videoInputDevices.length == 0));
    }

    end() {
        if (this.timer != null) {
            clearInterval(this.timer);
        }
        this.session.audioVideo.audioMixController.unbindAudioElement();
        this.session.audioVideo.stopVideoInput();
        this.session.audioVideo.stopAudioInput();
        this.session.audioVideo.stopLocalVideoTile();
        this.isEnded = true;
    }

    log(title = null, message = null) {
        if (this.getElement('log').length == 0) {
            return;
        }
        let text = '';
        if (title != null) {
            text = title + ': ';
        }
        if (message != null) {
            text = text + message;
        }
        this.logs.push(text);
    }

    error(message) {
        if (this.getElement('log').length == 0) {
            return;
        }
        this.errors.push(message);
        (this.sendLogCallback != null) && this.sendLogCallback(this.getValue('log'), this.getValue('id'), this);
    }

}